import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import {
  HapticNotificationFeedbackStyle,
  triggerHapticNotificationFeedback,
  useNoticeBannersStackContext,
  useResponsive,
} from '@sg/garnish';
import { type Order } from '@sg/graphql-schema';

import { type AddUpsellToBagParams, type AddUpsellToBagResponse } from '@order/features-ordering';
import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import {
  AddUpsellToBagDocument,
  type AddUpsellToBagMutation,
  type AddUpsellToBagMutationVariables,
} from '../../GraphQL/BagUpsells.generated';
import { prepareCart } from '../../utils/prepareCart';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for adding an upsell to the bag through the ordering machine.
 */
export const useAddUpsellToBag = () => {
  const client = useClient();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { track } = useTelemetry();
  const { currentBreakpoint } = useResponsive();

  // ─── Mutations ────────────────────────────────────────────────────────────

  const addUpsellToBag = useMemo(
    () =>
      fromPromise<AddUpsellToBagResponse, AddUpsellToBagParams>(async ({ input }) => {
        const { productId, productName, cart, deliveryDetails, placementVariation } = input;
        const quantity = input.quantity ?? 1;

        const mutation = client.mutation<AddUpsellToBagMutation, AddUpsellToBagMutationVariables>;

        const response = await mutation(
          AddUpsellToBagDocument,
          {
            input: {
              productId: productId,
              deliveryOrderDetails: deliveryDetails?.addressId
                ? {
                    addressId: deliveryDetails.addressId,
                    deliveryFee: deliveryDetails.deliveryFee,
                    tip: deliveryDetails.tip,
                    vendor: deliveryDetails.vendor,
                    vendorRestaurantId: deliveryDetails.vendorRestaurantId,
                  }
                : undefined,
              quantity,
              customName: null,
              additions: [],
              removals: [],
              substitutions: [],
              mixedDressingDetails: [],
            },
          },
          { requestPolicy: 'network-only' },
        ).toPromise();

        const result = response.data?.addLineItemToCart;

        if (result?.__typename === 'AddLineItemToCartSuccess') {
          void triggerHapticNotificationFeedback(HapticNotificationFeedbackStyle.Success);

          track('category_upsells.one_click_add_to_bag_submit', {
            selectedProduct: {
              id: productId,
              name: productName,
              restaurantId: cart.restaurantId,
              location: cart.restaurantSlug,
              quantity,
            },
          });

          if (placementVariation) {
            track('bag_upsell.product_added_to_bag', {
              quantity,
              product: telemetryProductFromInput({
                id: productId,
                name: productName,
                restaurantId: cart.restaurantId,
                location: cart.restaurantSlug,
                quantity,
              }),
              placementVariation,
            });
          }

          if (!currentBreakpoint.isXS) {
            addNoticeBanner({
              palette: 'success',
              text: formatMessage(messages.oneClickSuccess, {
                quantity,
                product_name: productName,
              }),
              autoHideTimeout: 1800,
            });
          }

          return { cart: prepareCart(result.cart as Partial<Order>) };
        }

        if (result?.__typename === 'OrderLocked') {
          const error = formatMessage(messages.orderLocked);

          addNoticeBanner({ text: error, palette: 'neutral' });
          track('category_upsells.one_click_add_to_bag_fail', { message: error });

          return { error };
        }

        const possibleError = result as Readonly<{ message?: string }>;
        const errorMessage = getUrqlError(response.error, possibleError);
        const fallbackError = formatMessage(messages.genericError);
        const error = errorMessage ?? fallbackError;

        addNoticeBanner({
          palette: 'caution',
          text: error,
          autoHideTimeout: 1800,
        });

        track('category_upsells.one_click_add_to_bag_fail', {
          message: error,
        });

        return { error };
      }),
    [currentBreakpoint.isXS, client.mutation, addNoticeBanner, formatMessage, track],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { addUpsellToBag };
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  oneClickSuccess: {
    defaultMessage: '{quantity}x {product_name} added to your bag!',
    description: 'Bag | Upsell | One-click add to bag success message',
  },
  genericError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Bag | Upsell | Generic error',
  },
  orderLocked: {
    defaultMessage: 'Cannot add item because group order is locked while owner is at checkout.',
    description: 'Bag | Upsell | Group order locked',
  },
});
